import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Building2,
  UserPlus,
  Puzzle,
  CreditCard,
  ClipboardCheck,
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Info,
  Mail,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Badge } from '@/app/components/ui/badge';
import { Switch } from '@/app/components/ui/switch';
import { Slider } from '@/app/components/ui/slider';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';

import {
  useCreatePlatformOrganization,
  usePlatformBillingPlans,
  useWizardUpdateOrganization,
} from '@/hooks/use-platform';
import { handleApiError } from '@/lib/api-client';
import { toast } from '@/app/components/ui/simple-toaster';
import { computePlan } from '@/lib/pricing';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type {
  PlatformOrganizationCreatePayload,
  PlatformOrganizationCreateResponse,
  SubscriptionPlan,
} from '@/lib/types';

// ─── Constants ───────────────────────────────────────────────────────────────

const OPERATING_MODELS = [
  { value: 'field_ops', label: 'Field Operations', description: 'Rope-access contractors who travel to client sites' },
  { value: 'facility_ops', label: 'Facility Operations', description: 'Venue/building owners with on-site teams' },
  { value: 'training_ops', label: 'Training Provider', description: 'IRATA/SPRAT training centres running courses at their own venues' },
  { value: 'consultant', label: 'Consultant / Assessor', description: 'Specialising in documents, audits and assessments across client sites' },
  { value: 'hybrid', label: 'Hybrid Operations', description: 'Combines field, facility and consulting capabilities — all features enabled' },
] as const;

// Mirrors backend.compliance.scope_defaults.INDUSTRY_COMPLIANCE_DEFAULTS for
// the create-org UI. Keep in sync with the backend source if those defaults change.
const SCOPE_FLAG_DEFAULTS: Record<string, { is_operator: boolean; is_trainer: boolean; operates_venues: boolean }> = {
  field_ops: { is_operator: true, is_trainer: false, operates_venues: false },
  facility_ops: { is_operator: true, is_trainer: false, operates_venues: false },
  training_ops: { is_operator: true, is_trainer: true, operates_venues: true },
  consultant: { is_operator: false, is_trainer: false, operates_venues: false },
  hybrid: { is_operator: true, is_trainer: true, operates_venues: true },
};

const SCOPE_FLAG_LABELS = {
  is_operator: 'Operator (O)',
  is_trainer: 'Trainer (T)',
  operates_venues: 'Operates training venues (V)',
};

const TIME_ZONES = ['UTC', ...Intl.supportedValuesOf('timeZone')];

interface ModuleDefinition {
  key: string;
  name: string;
  description: string;
  category: string;
  recommends: string[];
}

const MODULES: ModuleDefinition[] = [
  {
    key: 'inventory',
    name: 'H3Gear',
    description: 'Inventory & asset management — tracking, inspections, deployments',
    category: 'inventory',
    recommends: [],
  },
  {
    key: 'safety',
    name: 'H3Safe',
    description: 'Safety compliance — JSA/FLHA, toolbox talks, near-miss reporting',
    category: 'safety',
    recommends: [],
  },
  {
    key: 'crew',
    name: 'H3Crew',
    description: 'Workforce management — tech profiles, certifications, scheduling',
    category: 'people',
    recommends: [],
  },
  {
    key: 'projects',
    name: 'H3Books',
    description: 'Billing & project finances — estimates, bids, invoicing, change orders',
    category: 'operations',
    recommends: ['crew'],
  },
  {
    key: 'work',
    name: 'H3Project',
    description: 'Work management — tasks/checklists under foundation Projects',
    category: 'operations',
    recommends: ['projects'],
  },
  {
    key: 'payroll',
    name: 'H3Pay',
    description: 'Payroll & expenses — timesheets, expense tracking, payroll export',
    category: 'finance',
    recommends: ['crew', 'projects'],
  },
  {
    key: 'compliance',
    name: 'H3Comply',
    description: 'Compliance management — audit prep, management reviews, safety KPIs, incidents',
    category: 'compliance',
    recommends: ['safety', 'crew'],
  },
];

const MODULE_COLORS: Record<string, string> = {
  inventory: 'bg-blue-100 text-blue-800',
  safety: 'bg-green-100 text-green-800',
  people: 'bg-purple-100 text-purple-800',
  operations: 'bg-amber-100 text-amber-800',
  finance: 'bg-emerald-100 text-emerald-800',
  compliance: 'bg-indigo-100 text-indigo-800',
};

const STEPS = [
  { label: 'Company Details', icon: Building2 },
  { label: 'Admin Invite', icon: UserPlus },
  { label: 'Modules', icon: Puzzle },
  { label: 'Plan & Trial', icon: CreditCard },
  { label: 'Review & Create', icon: ClipboardCheck },
] as const;

// ─── Form Data Types ─────────────────────────────────────────────────────────

interface WizardFormData {
  // Step 1
  name: string;
  industry: string;
  is_demo: boolean;
  is_operator: boolean;
  is_trainer: boolean;
  operates_venues: boolean;
  team_size: number;
  time_zone: string;
  // Step 2
  admin_email: string;
  // Step 3
  enabled_modules: string[];
  // Step 4
  plan_slug: string;
  trial_days: number;
}

interface FieldErrors {
  [key: string]: string;
}

// ─── Step Components ─────────────────────────────────────────────────────────

function StepCompanyDetails({
  data,
  errors,
  onChange,
}: {
  data: WizardFormData;
  errors: FieldErrors;
  onChange: (field: keyof WizardFormData, value: string | number | boolean) => void;
}) {
  const prevIndustryRef = useRef(data.industry);
  const [demoConfirmOpen, setDemoConfirmOpen] = useState(false);
  const [pendingDemoValue, setPendingDemoValue] = useState(data.is_demo);

  useEffect(() => {
    if (!data.industry) return;
    if (prevIndustryRef.current === data.industry) return;
    const defaults = SCOPE_FLAG_DEFAULTS[data.industry];
    if (!defaults) return;
    onChange('is_operator', defaults.is_operator);
    onChange('is_trainer', defaults.is_trainer);
    onChange('operates_venues', defaults.operates_venues);
    prevIndustryRef.current = data.industry;
  }, [data.industry, onChange]);

  return (
    <div className="space-y-6">
      <div>
        <CardTitle>Company Details</CardTitle>
        <CardDescription className="mt-1">Basic information about the new organization.</CardDescription>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="org-name">
            Organization Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="org-name"
            placeholder="e.g. Apex Rope Access Ltd."
            value={data.name}
            onChange={(e) => onChange('name', e.target.value)}
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="team-size">
            Team Size <span className="text-red-500">*</span>
          </Label>
          <Input
            id="team-size"
            type="number"
            min={1}
            placeholder="e.g. 15"
            value={data.team_size || ''}
            onChange={(e) => onChange('team_size', Math.max(0, Number(e.target.value) || 0))}
            className={errors.team_size ? 'border-red-500' : ''}
          />
          {errors.team_size && <p className="text-sm text-red-600">{errors.team_size}</p>}
        </div>

        <div className="space-y-2">
          <Label>Operating Model <span className="text-red-500">*</span></Label>
          <div className="space-y-2">
            {OPERATING_MODELS.map((model) => (
              <div
                key={model.value}
                onClick={() => onChange('industry', model.value)}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  data.industry === model.value
                    ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-400'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                  data.industry === model.value
                    ? 'border-blue-600 bg-blue-600'
                    : 'border-gray-300'
                }`}>
                  {data.industry === model.value && (
                    <div className="h-1.5 w-1.5 rounded-full bg-white" />
                  )}
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-900">{model.label}</span>
                  <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                </div>
              </div>
            ))}
          </div>
          {errors.industry && <p className="text-sm text-red-600">{errors.industry}</p>}
        </div>

        <div className="space-y-2">
          <Label>Compliance scope</Label>
          <CardDescription className="text-xs">
            Select the IRATA / SPRAT scope tags that apply. Defaults are set by the operating model above.
          </CardDescription>
          <div className="space-y-2">
            {(Object.keys(SCOPE_FLAG_LABELS) as Array<keyof typeof SCOPE_FLAG_LABELS>).map((key) => (
              <div key={key} className="flex items-center gap-3">
                <Checkbox
                  id={`scope-${key}`}
                  checked={data[key]}
                  onCheckedChange={(checked) => onChange(key, checked === true)}
                />
                <Label htmlFor={`scope-${key}`} className="cursor-pointer text-sm font-normal text-gray-700">
                  {SCOPE_FLAG_LABELS[key]}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-time-zone">Time zone</Label>
          <select
            id="org-time-zone"
            value={data.time_zone}
            onChange={(event) => onChange('time_zone', event.target.value)}
            className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Confirm from the customer browser during onboarding</option>
            {TIME_ZONES.map((timeZone) => (
              <option key={timeZone} value={timeZone}>
                {timeZone}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500">
            Optional. Leave blank so the customer confirms their browser&apos;s zone during onboarding.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 p-4">
          <div>
            <Label htmlFor="demo-organization" className="text-sm font-medium">
              Demo org
            </Label>
            <p className="text-xs text-gray-500">
              Use in-app delivery for demo organization messaging.
            </p>
          </div>
          <Switch
            id="demo-organization"
            checked={data.is_demo}
            onCheckedChange={(checked) => {
              setPendingDemoValue(checked);
              setDemoConfirmOpen(true);
            }}
          />
        </div>
      </div>

      <AlertDialog open={demoConfirmOpen} onOpenChange={setDemoConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDemoValue ? 'Enable' : 'Disable'} Demo org?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Demo orgs never receive scheduled reminders or digests, and JSA signature emails are in-app only.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onChange('is_demo', pendingDemoValue);
                setDemoConfirmOpen(false);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StepAdminAccount({
  data,
  errors,
  onChange,
}: {
  data: WizardFormData;
  errors: FieldErrors;
  onChange: (field: keyof WizardFormData, value: string) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <CardTitle>Admin Invitation</CardTitle>
        <CardDescription className="mt-1">
          Invite the first administrator to create their own account.
        </CardDescription>
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin-email">
          Email <span className="text-red-500">*</span>
        </Label>
        <Input
          id="admin-email"
          type="email"
          placeholder="admin@company.com"
          value={data.admin_email}
          onChange={(e) => onChange('admin_email', e.target.value)}
          className={errors.admin_email ? 'border-red-500' : ''}
        />
        {errors.admin_email && <p className="text-sm text-red-600">{errors.admin_email}</p>}
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <p className="text-sm text-blue-900">
          An invite link will be emailed to{' '}
          <strong>{data.admin_email.trim() || 'the administrator'}</strong>.
        </p>
        <p className="mt-1 text-xs text-blue-700">
          They will choose their username and password when accepting the invitation.
        </p>
      </div>
    </div>
  );
}

function StepModules({
  data,
  onChange,
}: {
  data: WizardFormData;
  onChange: (field: keyof WizardFormData, value: string[]) => void;
}) {
  const enabledSet = useMemo(() => new Set(data.enabled_modules), [data.enabled_modules]);

  const getRecommendationHint = useCallback(
    (mod: (typeof MODULES)[number]) => {
      if (mod.recommends.length === 0) return null;
      const missing = mod.recommends.filter((dep) => !enabledSet.has(dep));
      if (missing.length === 0) return null;
      const names = missing.map((key) => MODULES.find((m) => m.key === key)?.name ?? key);
      return `Works better with ${names.join(' and ')}`;
    },
    [enabledSet]
  );

  const handleToggle = useCallback(
    (modKey: string, checked: boolean) => {
      const next = new Set(enabledSet);

      if (checked) {
        next.add(modKey);
      } else {
        next.delete(modKey);
      }

      onChange('enabled_modules', Array.from(next));
    },
    [enabledSet, onChange]
  );

  const handleEnableAll = () => {
    onChange(
      'enabled_modules',
      MODULES.map((m) => m.key)
    );
  };

  const handleDisableAll = () => {
    onChange('enabled_modules', []);
  };

  const allEnabled = MODULES.every((m) => enabledSet.has(m.key));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <CardTitle>Modules</CardTitle>
          <CardDescription className="mt-1">
            Choose which capability packs to enable for this organization.
          </CardDescription>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={allEnabled ? handleDisableAll : handleEnableAll}
        >
          {allEnabled ? 'Disable All' : 'Enable All'}
        </Button>
      </div>

      {/* H3ightboard — always included */}
      <div className="flex items-start gap-4 p-4 rounded-lg border border-green-200 bg-green-50/50">
        <Switch checked disabled className="mt-0.5 opacity-60" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">H3ightboard</span>
            <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
              Always Included
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Free operations board — drag-and-drop staffing, planning, sticky notes.
          </p>
        </div>
        <span className="text-sm font-medium text-green-700 whitespace-nowrap">Free</span>
      </div>

      <div className="space-y-3">
        {MODULES.map((mod) => {
          const isEnabled = enabledSet.has(mod.key);
          const recHint = getRecommendationHint(mod);

          return (
            <div
              key={mod.key}
              className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                isEnabled ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-white'
              }`}
            >
              <Switch
                checked={isEnabled}
                onCheckedChange={(checked) => handleToggle(mod.key, checked)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{mod.name}</span>
                  <Badge
                    variant="secondary"
                    className={`text-xs ${MODULE_COLORS[mod.category] ?? 'bg-gray-100 text-gray-800'}`}
                  >
                    {mod.category}
                  </Badge>
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{mod.description}</p>

                {recHint && isEnabled && (
                  <div className="flex items-center gap-1 mt-1.5">
                    <Info className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-blue-600">{recHint}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepPlanTrial({
  data,
  onChange,
  plans,
  plansLoading,
}: {
  data: WizardFormData;
  onChange: (field: keyof WizardFormData, value: number | string) => void;
  plans: SubscriptionPlan[];
  plansLoading: boolean;
}) {
  const computed = useMemo(
    () => computePlan(data.enabled_modules, plans, data.team_size),
    [data.enabled_modules, plans, data.team_size]
  );

  return (
    <div className="space-y-6">
      <div>
        <CardTitle>Plan & Trial</CardTitle>
        <CardDescription className="mt-1">
          Monthly subscription pricing based on the modules selected.
        </CardDescription>
      </div>

      <div className="space-y-6">
        {/* Computed Plan Display */}
        <div className="space-y-2">
          <Label>Subscription Plan</Label>
          {plansLoading ? (
            <p className="text-sm text-gray-400">Loading pricing...</p>
          ) : !computed ? (
            <p className="text-sm text-gray-500">
              {data.enabled_modules.length === 0
                ? 'No modules selected — no plan required.'
                : 'No pricing available. Please check the plan catalog.'}
            </p>
          ) : (
            <div className="p-4 rounded-lg border border-blue-400 bg-blue-50/50 ring-1 ring-blue-400">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900">{computed.label}</span>
                  <p className="text-xs text-gray-500 mt-0.5">Monthly billing</p>
                </div>
                <span className="text-lg font-semibold text-gray-900">
                  ${computed.priceMonthly}/mo
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Trial Duration */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Trial Duration</Label>
            <span className="text-sm font-medium text-blue-600">
              {data.trial_days === 0
                ? 'No trial'
                : `${data.trial_days} day${data.trial_days !== 1 ? 's' : ''}`}
            </span>
          </div>
          <Slider
            value={[data.trial_days]}
            onValueChange={(vals) => onChange('trial_days', vals[0])}
            min={0}
            max={90}
            step={1}
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>No trial</span>
            <span>30 days</span>
            <span>60 days</span>
            <span>90 days</span>
          </div>
        </div>

        {/* Quick-select buttons */}
        <div className="flex flex-wrap gap-2">
          {[0, 7, 14, 30, 60, 90].map((days) => (
            <Button
              key={days}
              type="button"
              variant={data.trial_days === days ? 'default' : 'outline'}
              size="sm"
              onClick={() => onChange('trial_days', days)}
            >
              {days === 0 ? 'No trial' : `${days}d`}
            </Button>
          ))}
        </div>

        {data.trial_days > 0 && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
            <p className="text-sm text-blue-800">
              Trial will expire on{' '}
              <strong>
                {formatDateTimeInTimeZone(
                  // eslint-disable-next-line react-hooks/purity
                  new Date(Date.now() + data.trial_days * 24 * 60 * 60 * 1000).toISOString(),
                  data.time_zone,
                  {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  }
                )}
              </strong>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepReview({
  data,
  plans,
}: {
  data: WizardFormData;
  plans: SubscriptionPlan[];
}) {
  const computed = computePlan(data.enabled_modules, plans, data.team_size);
  const enabledModuleNames = data.enabled_modules
    .map((key) => MODULES.find((m) => m.key === key)?.name ?? key)
    .sort();

  return (
    <div className="space-y-6">
      <div>
        <CardTitle>Review & Create</CardTitle>
        <CardDescription className="mt-1">
          Review the details below before creating the organization.
        </CardDescription>
      </div>

      {/* Company Details */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
          Company Details
        </h3>
        <div className="p-4 rounded-lg border border-gray-200 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Organization Name</span>
            <span className="text-sm font-medium text-gray-900">{data.name}</span>
          </div>
          {data.industry && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Operating Model</span>
              <span className="text-sm font-medium text-gray-900">
                {OPERATING_MODELS.find((m) => m.value === data.industry)?.label ?? data.industry}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Compliance Scope</span>
            <span className="text-sm font-medium text-gray-900">
              {[
                data.is_operator && SCOPE_FLAG_LABELS.is_operator,
                data.is_trainer && SCOPE_FLAG_LABELS.is_trainer,
                data.operates_venues && SCOPE_FLAG_LABELS.operates_venues,
              ]
                .filter(Boolean)
                .join(', ') || 'None selected'}
            </span>
          </div>
          {data.team_size > 0 && (
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Team Size</span>
              <span className="text-sm font-medium text-gray-900">{data.team_size} people</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Time Zone</span>
            <span className="text-sm font-medium text-gray-900">
              {data.time_zone || 'Customer confirms during onboarding'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Demo Org</span>
            <span className="text-sm font-medium text-gray-900">
              {data.is_demo ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </div>

      {/* Admin Invitation */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
          Admin Invitation
        </h3>
        <div className="p-4 rounded-lg border border-gray-200 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm text-gray-500">Email</span>
            <span className="text-sm font-medium text-gray-900">{data.admin_email}</span>
          </div>
          <p className="text-sm text-gray-600">
            An invite link will be emailed to {data.admin_email}. The administrator will choose
            their username and password.
          </p>
        </div>
      </div>

      {/* Modules */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Modules</h3>
        <div className="p-4 rounded-lg border border-gray-200">
          {enabledModuleNames.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {enabledModuleNames.map((name) => (
                <Badge key={name} variant="secondary" className="bg-blue-100 text-blue-800">
                  {name}
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No modules selected</p>
          )}
        </div>
      </div>

      {/* Plan & Trial */}
      {computed && (
        <div className="space-y-2">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
            Plan & Trial
          </h3>
          <div className="p-4 rounded-lg border border-gray-200 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Plan</span>
              <span className="text-sm font-medium text-gray-900">
                {computed.label} — ${computed.priceMonthly}/mo
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-500">Trial Period</span>
              <span className="text-sm font-medium text-gray-900">
                {data.trial_days === 0 ? 'No trial' : `${data.trial_days} days`}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Invitation Email */}
      <div className="flex items-start gap-3 p-4 rounded-lg border border-blue-200 bg-blue-50">
        <Mail className="h-4 w-4 mt-0.5 text-blue-600" />
        <div>
          <p className="text-sm font-medium text-blue-900">Admin invitation will be emailed</p>
          <p className="text-xs text-blue-700 mt-0.5">
            The email includes enabled modules, trial end date, and a quick-start checklist.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Success Screen ──────────────────────────────────────────────────────────

function SuccessScreen({
  result,
}: {
  result: PlatformOrganizationCreateResponse;
}) {
  const navigate = useNavigate();

  return (
    <div className="max-w-lg mx-auto text-center space-y-6">
      <div className="flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-gray-900">Organization Created!</h2>
        <p className="text-gray-600 mt-2">
          <strong>{result.name}</strong> has been successfully provisioned.
        </p>
      </div>

      {/* Org Details */}
      <Card>
        <CardContent className="pt-6 space-y-3 text-left">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Organization ID</span>
            <span className="font-mono text-gray-900">{result.id}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Admin Email</span>
            <span className="text-gray-900">{result.admin_email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Invitation Status</span>
            <Badge variant="secondary" className="capitalize">
              {result.admin_invitation?.status ?? 'pending'}
            </Badge>
          </div>
          {result.invitation_email_dispatched ? (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-100">
              <Check className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">
                An invite link was queued for delivery to {result.admin_email}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 border border-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800">
                {result.email_configured
                  ? 'Invitation email was not queued — resend it from the organization detail page'
                  : 'Email is not configured — resend after email delivery is configured'}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex gap-3 justify-center">
        <Button variant="outline" onClick={() => navigate('/platform-admin/organizations')}>
          Back to Organizations
        </Button>
        <Button onClick={() => navigate('/platform-admin/organizations/new')}>
          Create Another
        </Button>
      </div>
    </div>
  );
}

// ─── Main Wizard Component ───────────────────────────────────────────────────

export function CreateOrganizationPage() {
  const createOrg = useCreatePlatformOrganization();
  const wizardUpdate = useWizardUpdateOrganization();
  const { data: plans = [], isLoading: plansLoading } = usePlatformBillingPlans();

  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [result, setResult] = useState<PlatformOrganizationCreateResponse | null>(null);
  const [createdOrgId, setCreatedOrgId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<WizardFormData>({
    name: '',
    industry: '',
    is_demo: false,
    is_operator: true,
    is_trainer: false,
    operates_venues: false,
    team_size: 0,
    time_zone: '',
    admin_email: '',
    enabled_modules: [],
    plan_slug: '',
    trial_days: 14,
  });

  const handleChange = useCallback(
    (field: keyof WizardFormData, value: string | string[] | number | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    []
  );

  // ─── Validation ────────────────────────────────────────────────────────────

  const validateStep = useCallback(
    (stepIndex: number): boolean => {
      const newErrors: FieldErrors = {};

      if (stepIndex === 0) {
        if (!formData.name.trim()) newErrors.name = 'Organization name is required';
        if (!formData.industry) newErrors.industry = 'Operating model is required';
        if (!formData.team_size || formData.team_size < 1) {
          newErrors.team_size = 'Team size is required';
        }
      }

      if (stepIndex === 1) {
        if (!formData.admin_email.trim()) {
          newErrors.admin_email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
          newErrors.admin_email = 'Enter a valid email address';
        }
      }

      // Steps 2, 3, 4 have no required validation (modules optional, trial optional)

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData]
  );

  // ─── Step-level persistence ────────────────────────────────────────────────

  const handleNext = useCallback(async () => {
    if (!validateStep(step)) return;

    setServerError(null);
    setSaving(true);

    try {
      // Step 1 → 2: no persistence needed (step 1 data saved when org is created)
      if (step === 0) {
        setStep(1);
        return;
      }

      // Step 2 → 3: create the org record with steps 1+2 data
      if (step === 1) {
        let organizationId = createdOrgId;

        if (organizationId === null) {
          const payload: PlatformOrganizationCreatePayload = {
            name: formData.name.trim(),
            industry: formData.industry,
            is_demo: formData.is_demo,
            is_operator: formData.is_operator,
            is_trainer: formData.is_trainer,
            operates_venues: formData.operates_venues,
            admin_email: formData.admin_email.trim(),
            enabled_modules: [],
            plan_slug: '',
            trial_days: 0,
            send_invitation_email: false,
            ...(formData.time_zone ? { time_zone: formData.time_zone } : {}),
          };

          const response = await createOrg.mutateAsync(payload);
          organizationId = response.id;
          setCreatedOrgId(response.id);
        }

        toast.success('Organization record created — progress will be saved as you continue.');
        setStep(2);
        return;
      }

      // Step 3 → 4 (or skip to 5 if 0 modules): save modules
      if (step === 2 && createdOrgId !== null) {
        await wizardUpdate.mutateAsync({
          orgId: createdOrgId,
          enabled_modules: formData.enabled_modules,
        });
        // Skip plan step if no modules selected
        if (formData.enabled_modules.length === 0) {
          // Clear any previously-saved plan & trial data
          await wizardUpdate.mutateAsync({
            orgId: createdOrgId,
            plan_slug: '',
            trial_days: 0,
          });
          setStep(4);
        } else {
          setStep(3);
        }
        return;
      }

      // Step 4 → 5: save plan & trial (auto-compute plan_slug from modules)
      if (step === 3 && createdOrgId !== null) {
        const payload: {
          orgId: number;
          plan_slug: string;
          trial_days: number;
        } = {
          orgId: createdOrgId,
          plan_slug: '',
          trial_days: formData.trial_days,
        };

        if (formData.trial_days > 0) {
          const computed = computePlan(
            formData.enabled_modules,
            plans,
            formData.team_size,
          );
          if (!computed) {
            setServerError(
              'No active plan is available for the selected modules. ' +
                'Please adjust your module selection.',
            );
            setSaving(false);
            return;
          }
          payload.plan_slug = computed.slug;
        }

        await wizardUpdate.mutateAsync(payload);
        setStep(4);
        return;
      }

      // Fallback: just advance
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch (err) {
      const apiErr = handleApiError(err);
      setServerError(apiErr.message);

      if (apiErr.errors) {
        const fieldMap: Record<string, string> = {};
        for (const [field, messages] of Object.entries(apiErr.errors)) {
          fieldMap[field] = messages[0];
        }
        setErrors(fieldMap);

        const step1Fields = ['name', 'industry'];
        const step2Fields = ['admin_email'];
        const errorFields = Object.keys(fieldMap);
        if (errorFields.some((f) => step1Fields.includes(f))) {
          setStep(0);
        } else if (errorFields.some((f) => step2Fields.includes(f))) {
          setStep(1);
        }
      }

      toast.error(apiErr.message);
    } finally {
      setSaving(false);
    }
  }, [
    step,
    formData,
    createOrg,
    wizardUpdate,
    createdOrgId,
    validateStep,
    plans,
  ]);

  const handleBack = useCallback(() => {
    // Prevent going back to steps 1 or 2 after the org has been created
    if (createdOrgId !== null && step <= 2) return;
    // Skip plan step going backward if 0 modules
    if (step === 4 && formData.enabled_modules.length === 0) {
      setStep(2);
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
  }, [createdOrgId, step, formData.enabled_modules.length]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(step)) return;
    if (createdOrgId === null) return;

    setServerError(null);
    setSaving(true);

    try {
      const response = await wizardUpdate.mutateAsync({
        orgId: createdOrgId,
        send_invitation_email: true,
      });
      setResult(response);
      toast.success(`Organization "${response.name}" setup complete!`);
    } catch (err) {
      const apiErr = handleApiError(err);
      setServerError(apiErr.message);
      toast.error(apiErr.message);
    } finally {
      setSaving(false);
    }
  }, [step, wizardUpdate, createdOrgId, validateStep]);

  // ─── If we have a result, show success ─────────────────────────────────────

  if (result) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <SuccessScreen result={result} />
      </div>
    );
  }

  // ─── Render Wizard ─────────────────────────────────────────────────────────

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/platform-admin/organizations">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Organizations
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Create Organization</h1>
        <p className="text-gray-600 mt-1">
          Provision a new customer organization with an admin invitation, modules, and trial.
        </p>
      </div>

      {/* Step Indicator */}
      <nav className="flex items-center justify-between">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === step;
          const isCompleted = i < step;

          return (
            <div key={s.label} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1">
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                    isCompleted
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : isActive
                        ? 'border-blue-600 bg-white text-blue-600'
                        : 'border-gray-300 bg-white text-gray-400'
                  }`}
                >
                  {isCompleted ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:block ${
                    isActive ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-400'
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-blue-600' : 'bg-gray-200'}`}
                />
              )}
            </div>
          );
        })}
      </nav>

      {/* Step Content */}
      <Card>
        <CardHeader className="pb-2" />
        <CardContent>
          {step === 0 && (
            <StepCompanyDetails data={formData} errors={errors} onChange={handleChange} />
          )}
          {step === 1 && (
            <StepAdminAccount data={formData} errors={errors} onChange={handleChange} />
          )}
          {step === 2 && <StepModules data={formData} onChange={handleChange} />}
          {step === 3 && <StepPlanTrial data={formData} onChange={handleChange} plans={plans} plansLoading={plansLoading} />}
          {step === 4 && <StepReview data={formData} plans={plans} />}

          {/* Server error */}
          {serverError && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
              <p className="text-sm text-red-700">{serverError}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={step === 0 || saving || (createdOrgId !== null && step <= 2)}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        {step < STEPS.length - 1 ? (
          <Button onClick={handleNext} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finishing...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-1" />
                Complete Setup
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
